import type { PlatformAiUsageBreakdown, PlatformAiUsagePage } from '@livechat/shared';
import { useEffect, useMemo, useState } from 'react';

import { getPlatformAiUsage } from '../api.js';
import { DateRangePicker, presetDateRange, type DateRangeValue } from './DateRangePicker.js';

const PAGE_SIZE = 25;

const costFormatter = new Intl.NumberFormat('en-US', {
	currency: 'USD',
	maximumFractionDigits: 12,
	minimumFractionDigits: 2,
	style: 'currency',
});

const tableCostFormatter = new Intl.NumberFormat('en-US', {
	currency: 'USD',
	maximumFractionDigits: 4,
	minimumFractionDigits: 4,
	style: 'currency',
});

export function aiActionLabel(action: string): string {
	if (action === 'ai_chat_rating') return 'AI Chat Rating';
	if (action === 'ai_conversation_insights') return 'AI Conversation Insights';
	const label = action.replaceAll(/[_.-]+/g, ' ');
	return label ? `${label[0]!.toUpperCase()}${label.slice(1)}` : action;
}

function formatCost(cost: number): string {
	return costFormatter.format(cost);
}

function formatTableCost(cost: number): string {
	return tableCostFormatter.format(cost);
}

function rangeBoundary(range: DateRangeValue, boundary: 'from' | 'to'): string {
	const date = boundary === 'from' ? range.from : range.to;
	const time = boundary === 'from' ? range.fromTime : range.toTime;
	return new Date(`${date}T${time}:${boundary === 'from' ? '00.000' : '59.999'}`).toISOString();
}

function BreakdownList({ items, label }: { items: PlatformAiUsageBreakdown[]; label: string }) {
	return (
		<article className="ai-usage-breakdown-card">
			<header>
				<div>
					<span>{label}</span>
					<strong>{items.length.toLocaleString()}</strong>
				</div>
				<small>
					{items.reduce((total, item) => total + item.calls, 0).toLocaleString()} calls
				</small>
			</header>
			{items.length ? (
				<ul>
					{items.slice(0, 6).map((item) => (
						<li key={item.name}>
							<span title={item.name}>
								{label === 'AI actions' ? aiActionLabel(item.name) : item.name}
							</span>
							<small>
								{item.calls.toLocaleString()} · {formatCost(item.costCredits)}
							</small>
						</li>
					))}
				</ul>
			) : (
				<p>No usage in this date range.</p>
			)}
			{items.length > 6 && <footer>+{items.length - 6} more</footer>}
		</article>
	);
}

export function PlatformAiUsage() {
	const [range, setRange] = useState(() => presetDateRange());
	const [tenantId, setTenantId] = useState('');
	const [action, setAction] = useState('');
	const [page, setPage] = useState(1);
	const [result, setResult] = useState<PlatformAiUsagePage>();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();
	const [reload, setReload] = useState(0);

	useEffect(() => {
		let current = true;
		setLoading(true);
		setError(undefined);
		void getPlatformAiUsage({
			...(action ? { action } : {}),
			from: rangeBoundary(range, 'from'),
			page,
			pageSize: PAGE_SIZE,
			...(tenantId ? { tenantId } : {}),
			to: rangeBoundary(range, 'to'),
		})
			.then((loaded) => {
				if (current) setResult(loaded);
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(caught instanceof Error ? caught.message : 'Could not load AI usage');
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [action, page, range, reload, tenantId]);

	const actionOptions = useMemo(
		() => [...new Set([...(result?.actions ?? []), ...(action ? [action] : [])])].sort(),
		[action, result?.actions],
	);
	const pageCount = Math.max(1, Math.ceil((result?.total ?? 0) / PAGE_SIZE));
	const first = result?.total ? (result.page - 1) * result.pageSize + 1 : 0;
	const last = result ? Math.min(result.total, result.page * result.pageSize) : 0;
	const summary = result?.summary;

	return (
		<section className="workspace-section ai-usage-page" aria-labelledby="ai-usage-heading">
			<div className="section-heading">
				<div>
					<p className="eyebrow">Platform AI</p>
					<h2 id="ai-usage-heading">AI Usage</h2>
					<p className="muted">
						Review OpenRouter costs, models, actions and token use across customers.
					</p>
				</div>
				<button
					className="secondary-button"
					disabled={loading}
					onClick={() => setReload((value) => value + 1)}
					type="button"
				>
					{loading ? 'Refreshing…' : 'Refresh'}
				</button>
			</div>

			<div className="ai-usage-toolbar">
				<DateRangePicker
					includeTime={false}
					onChange={(updatedRange) => {
						setPage(1);
						setRange(updatedRange);
					}}
					value={range}
				/>
				<label>
					<span>Customer</span>
					<select
						aria-label="Filter AI usage by customer"
						onChange={(event) => {
							setPage(1);
							setTenantId(event.currentTarget.value);
						}}
						value={tenantId}
					>
						<option value="">All customers</option>
						{result?.customers.map((customer) => (
							<option key={customer.id} value={customer.id}>
								{customer.name}
							</option>
						))}
					</select>
				</label>
				<label>
					<span>AI action</span>
					<select
						aria-label="Filter AI usage by action"
						onChange={(event) => {
							setPage(1);
							setAction(event.currentTarget.value);
						}}
						value={action}
					>
						<option value="">All actions</option>
						{actionOptions.map((option) => (
							<option key={option} value={option}>
								{aiActionLabel(option)}
							</option>
						))}
					</select>
				</label>
			</div>

			{error && (
				<div className="alert error">
					{error}{' '}
					<button onClick={() => setReload((value) => value + 1)} type="button">
						Try again
					</button>
				</div>
			)}

			<div className="ai-usage-metrics" aria-busy={loading}>
				<article>
					<span>Total amount used</span>
					<strong>{formatCost(summary?.totalCostCredits ?? 0)}</strong>
					<small>OpenRouter-reported cost in USD</small>
				</article>
				<article>
					<span>AI calls</span>
					<strong>{(summary?.calls ?? 0).toLocaleString()}</strong>
					<small>Successful metered requests</small>
				</article>
				<article>
					<span>Tokens used</span>
					<strong>{(summary?.totalTokens ?? 0).toLocaleString()}</strong>
					<small>
						{(summary?.inputTokens ?? 0).toLocaleString()} in ·{' '}
						{(summary?.outputTokens ?? 0).toLocaleString()} out
					</small>
				</article>
			</div>

			<div className="ai-usage-breakdowns" aria-busy={loading}>
				<BreakdownList items={summary?.models ?? []} label="Models used" />
				<BreakdownList items={summary?.actions ?? []} label="AI actions" />
			</div>

			<section aria-labelledby="ai-usage-totals-heading">
				<header className="table-section-heading">
					<h3 id="ai-usage-totals-heading">Total Usage</h3>
				</header>
				<div className="table-card paginated-table">
					<div
						className="ai-usage-table paginated-table-scroll"
						role="table"
						aria-busy={loading}
					>
						<div className="ai-usage-row ai-usage-head standard-table-head" role="row">
							<span role="columnheader">Customer</span>
							<span className="ai-usage-number" role="columnheader">
								AI calls
							</span>
							<span className="ai-usage-number" role="columnheader">
								Input tokens
							</span>
							<span className="ai-usage-number" role="columnheader">
								Output tokens
							</span>
							<span className="ai-usage-number" role="columnheader">
								Total tokens
							</span>
							<span className="ai-usage-number" role="columnheader">
								Total amount used
							</span>
						</div>
						{result?.items.map((customer) => (
							<div
								className="table-row ai-usage-row"
								key={customer.tenantId}
								role="row"
							>
								<span role="cell">{customer.tenantName}</span>
								<span className="ai-usage-number" role="cell">
									{customer.calls.toLocaleString()}
								</span>
								<span className="ai-usage-number" role="cell">
									{customer.inputTokens.toLocaleString()}
								</span>
								<span className="ai-usage-number" role="cell">
									{customer.outputTokens.toLocaleString()}
								</span>
								<span className="ai-usage-number" role="cell">
									{customer.totalTokens.toLocaleString()}
								</span>
								<span className="ai-usage-number" role="cell">
									{formatTableCost(customer.costCredits)}
								</span>
							</div>
						))}
						{loading && !result && <div className="empty-state">Loading AI usage…</div>}
						{!loading && !result?.items.length && (
							<div className="empty-state">
								No customer AI usage was recorded for these filters.
							</div>
						)}
					</div>
					<footer className="data-pagination">
						<span>
							Showing {first}–{last} of {result?.total ?? 0}
						</span>
						<div>
							<button
								aria-label="Previous page"
								disabled={loading || page <= 1}
								onClick={() => setPage((current) => Math.max(1, current - 1))}
								type="button"
							>
								‹
							</button>
							<span aria-live="polite">
								Page {page} of {pageCount}
							</span>
							<button
								aria-label="Next page"
								disabled={loading || page >= pageCount}
								onClick={() =>
									setPage((current) => Math.min(pageCount, current + 1))
								}
								type="button"
							>
								›
							</button>
						</div>
					</footer>
				</div>
			</section>
		</section>
	);
}
